/* eslint-disable react-refresh/only-export-components */
import React, { createContext, useContext, useState, useCallback, ReactNode } from "react";

type CacheEntry<T> = {
  data: T | null;
  timestamp: number;
  isLoading: boolean;
  error: Error | null;
};

type CacheStore = {
  [key: string]: CacheEntry<any>;
};

type Subscriber<T> = (data: T | null, isLoading: boolean, error: Error | null) => void;

type SubscriberMap = {
  [key: string]: Set<Subscriber<any>>;
};

interface ApiCacheContextType {
  getCache: <T>(key: string) => CacheEntry<T> | null;
  setCache: <T>(key: string, data: T | null, isLoading: boolean, error?: Error | null) => void;
  subscribe: <T>(key: string, callback: Subscriber<T>) => () => void;
  clearCache: (key?: string) => void;
}

const ApiCacheContext = createContext<ApiCacheContextType | undefined>(undefined);

export const ApiCacheProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [cache, setCache] = useState<CacheStore>({});
  const [subscribers, setSubscribers] = useState<SubscriberMap>({});

  const getCache = useCallback(
    <T,>(key: string): CacheEntry<T> | null => {
      return cache[key] || null;
    },
    [cache]
  );

  const setCacheValue = useCallback(
    <T,>(key: string, data: T | null, isLoading: boolean, error: Error | null = null) => {
      const entry: CacheEntry<T> = {
        data,
        timestamp: Date.now(),
        isLoading,
        error,
      };

      setCache(prev => ({ ...prev, [key]: entry }));

      // Notify all subscribers
      const subs = subscribers[key];
      if (subs) {
        subs.forEach(callback => callback(data, isLoading, error));
      }
    },
    [subscribers]
  );

  const subscribe = useCallback(<T,>(key: string, callback: Subscriber<T>) => {
    setSubscribers(prev => {
      const newSubs = { ...prev };
      if (!newSubs[key]) {
        newSubs[key] = new Set();
      }
      newSubs[key].add(callback);
      return newSubs;
    });

    // Return unsubscribe function
    return () => {
      setSubscribers(prev => {
        const newSubs = { ...prev };
        if (newSubs[key]) {
          newSubs[key].delete(callback);
          if (newSubs[key].size === 0) {
            delete newSubs[key];
          }
        }
        return newSubs;
      });
    };
  }, []);

  const clearCache = useCallback((key?: string) => {
    if (key) {
      setCache(prev => {
        const newCache = { ...prev };
        delete newCache[key];
        return newCache;
      });
    } else {
      setCache({});
    }
  }, []);

  const value: ApiCacheContextType = {
    getCache,
    setCache: setCacheValue,
    subscribe,
    clearCache,
  };

  return <ApiCacheContext.Provider value={value}>{children}</ApiCacheContext.Provider>;
};

export const useApiCache = (): ApiCacheContextType => {
  const context = useContext(ApiCacheContext);
  if (!context) {
    throw new Error("useApiCache must be used within ApiCacheProvider");
  }
  return context;
};
